import { supabase } from './supabase/supabase';
import { API_BASE_URL } from '@/lib/api-config';
import useGlobalContext from '@/hooks/useProjectContext';

/**
 * AI Service Configuration
 * Centralized service for all AI endpoints using Supabase Edge Functions
 * Supports dynamic switching between local and production environments
 */

// Environment detection
const isDevelopment = import.meta.env.DEV;
const isLocalSupabase = import.meta.env.VITE_SUPABASE_URL?.includes('127.0.0.1') || 
                       import.meta.env.VITE_SUPABASE_URL?.includes('localhost');

// AI Service URLs
const AI_SERVICE_URLS = {
  local: 'http://127.0.0.1:54321',
  production: 'https://uyucjjbotjqotulknkfy.supabase.co'
};

// Genkit Service URLs
const GENKIT_SERVICE_URLS = {
  local: API_BASE_URL,
  production: 'https://agent-service.chandru-vijayan101999.workers.dev'
};

// Get the appropriate base URL for Genkit
const getGenkitServiceBaseURL = (): string => {
  if (import.meta.env.VITE_GENKIT_SERVICE_URL) {
    return import.meta.env.VITE_GENKIT_SERVICE_URL;
  }
  
  if (isDevelopment || isLocalSupabase) {
    return GENKIT_SERVICE_URLS.local;
  }
  
  return GENKIT_SERVICE_URLS.production;
};

const GENKIT_BASE_URL = getGenkitServiceBaseURL();

// Get the appropriate base URL
const getAIServiceBaseURL = (): string => {
  // Allow manual override via environment variable
  if (import.meta.env.VITE_AI_SERVICE_URL) {
    return import.meta.env.VITE_AI_SERVICE_URL;
  }
  
  // Auto-detect based on Supabase URL or development mode
  if (isDevelopment || isLocalSupabase) {
    return AI_SERVICE_URLS.local;
  }
  
  return AI_SERVICE_URLS.production;
};

// Base AI service URL
const AI_BASE_URL = getAIServiceBaseURL();

console.log('🤖 AI Service Configuration:', {
  isDevelopment,
  isLocalSupabase,
  baseURL: AI_BASE_URL,
  supabaseURL: import.meta.env.VITE_SUPABASE_URL
});

/**
 * Get authentication headers for AI requests
 */
const getAuthHeaders = async (
  projectId?: string,
  storyId?: string,
  tenantId?: string
): Promise<Record<string, string>> => {
  const { data: { session } } = await supabase.auth.getSession();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add JWT token if available
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  // Get tenant ID and add to headers
  let finalTenantId = tenantId;
  if (!finalTenantId) {
    const { getCurrentTenantId } = await import('@/lib/auth-utils');
    const tenantIdResult = await getCurrentTenantId();
    finalTenantId = tenantIdResult || undefined;
  }

  if (finalTenantId) {
    headers['x-tenant-id'] = finalTenantId;
  }

  // Get fallback IDs from global context
  const globalStore = useGlobalContext.getState();
  const globalProjectId = globalStore.projectID?.toString();
  const globalStoryId = globalStore.storyID?.toString();
  const globalTenantId = globalStore.tenantID;

  // Add project and story headers if provided or available in global context
  const targetProjectId = projectId || globalProjectId;
  if (targetProjectId) {
    headers['x-project-id'] = targetProjectId;
  }

  const targetStoryId = storyId || globalStoryId;
  if (targetStoryId) {
    headers['x-story-id'] = targetStoryId;
  }
  
  // Use global tenant ID if not provided and not found in auth utils
  if (!headers['x-tenant-id'] && globalTenantId) {
    headers['x-tenant-id'] = globalTenantId;
  }

  return headers;
};

/**
 * Make authenticated request to AI service with retry logic
 */
const makeAIRequest = async (
  endpoint: string,
  body: any,
  options: {
    stream?: boolean;
    timeout?: number;
    retries?: number;
    projectId?: string;
    storyId?: string;
    tenantId?: string;
    baseUrl?: string;
  } = {}
): Promise<Response> => {
  const baseURL = options.baseUrl || AI_BASE_URL;
  const url = `${baseURL}${endpoint}`;
  const headers = await getAuthHeaders(options.projectId, options.storyId, options.tenantId);
  const maxRetries = options.retries || 1;
  const timeoutMs = options.timeout || (options.stream ? 300000 : 180000); // 5 min for streaming, 3 min for regular

  // Log request details for debugging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log(`🚀 AI Request: ${options.stream ? 'STREAMING' : 'STANDARD'}`, {
      url,
      bodyKeys: Object.keys(body),
      hasAuth: !!headers['Authorization'],
      timeout: `${timeoutMs / 1000}s`,
      maxRetries
    });
  }

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Create AbortController for timeout
    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();

        // Log error details for debugging (only in development)
        if (process.env.NODE_ENV === 'development') {
          console.error(`❌ AI Request failed (attempt ${attempt + 1}/${maxRetries + 1}):`, {
            status: response.status,
            statusText: response.statusText,
            error: errorText
          });
        }

        // Don't retry on client errors (4xx), only on server errors (5xx) or timeouts
        if (response.status < 500 && response.status !== 429) {
          // Provide user-friendly error messages for client errors
          if (response.status === 503) {
            throw new Error('AI service is temporarily unavailable. Please try again in a few moments.');
          } else if (response.status === 401) {
            throw new Error('Authentication failed. Please refresh the page and try again.');
          } else if (response.status === 403) {
            throw new Error('Access denied. Please check your subscription status.');
          } else if (response.status === 400) {
            throw new Error('Invalid request. Please check your input and try again.');
          } else {
            throw new Error(`Request failed. Please try again or contact support if the issue persists.`);
          }
        }

        // For server errors or rate limits, retry if we have attempts left
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // Exponential backoff, max 5s
          if (process.env.NODE_ENV === 'development') {
            console.log(`⏳ Retrying in ${delay}ms...`);
          }
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        // Final attempt failed - provide user-friendly messages
        if (response.status === 503) {
          throw new Error('AI service is temporarily unavailable. Please try again in a few moments.');
        } else if (response.status === 429) {
          throw new Error('Too many requests. Please wait a moment before trying again.');
        } else if (response.status >= 500) {
          throw new Error('AI service is experiencing issues. Please try again later.');
        } else {
          throw new Error('Request failed. Please try again or contact support if the issue persists.');
        }
      }

      return response;
    } catch (err: any) {
      clearTimeout(timeoutId);

      if (err instanceof Error && err.name === 'AbortError') {
        if (attempt < maxRetries) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`⏳ Request timed out, retrying (attempt ${attempt + 1}/${maxRetries + 1})...`);
          }
          const delay = Math.min(2000 * Math.pow(2, attempt), 10000); // Longer delay for timeouts
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        throw new Error('The AI service is taking longer than expected. Please try again with a shorter prompt or try again later.');
      }

      // Network errors
      if (err instanceof TypeError && err.message.includes('fetch')) {
        if (attempt < maxRetries) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`⏳ Network error, retrying (attempt ${attempt + 1}/${maxRetries + 1})...`);
          }
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        throw new Error('Unable to connect to AI service. Please check your internet connection and try again.');
      }

      // Don't retry on other errors
      throw err;
    }
  }

  // This should never be reached, but just in case
  throw new Error('Maximum retry attempts exceeded');
};

/**
 * AI Writing Actions
 */
export const aiWritingService = {
  /**
   * Process writing action (non-streaming)
   */
  async processAction(
    action: string,
    content: string,
    projectId: string,
    storyId: string,
    tenantId?: string,
    prompt?: string,
    options?: any
  ) {
    const body: any = { action, content };

    // Add prompt field if action is 'custom' (required) or if provided for other actions
    if (action === 'custom') {
      if (!prompt) {
        throw new Error('Prompt field is mandatory for custom action');
      }
      body.prompt = prompt;
    } else if (prompt) {
      body.prompt = prompt;
    }

    // Add options if provided (reserved for future use)
    if (options) {
      body.options = options;
    }

    const response = await makeAIRequest(
      '/api/writingAction',
      body,
      { projectId, storyId, tenantId, baseUrl: GENKIT_BASE_URL }
    );
    return response.json();
  },

  /**
   * Get autocomplete suggestion (non-streaming)
   */
  async autocomplete(
    cursorContext: string,
    chapterTitle: string,
    chapterDescription: string,
    chapterBeats: any[],
    projectId: string,
    storyId: string,
    tenantId?: string
  ) {
    const body: any = { 
      cursorContext, 
      chapterTitle, 
      chapterDescription, 
      chapterBeats,
      maxTokens: 500
    };

    const response = await makeAIRequest(
      '/api/agent/autocomplete',
      body,
      { projectId, storyId, tenantId, baseUrl: GENKIT_BASE_URL }
    );
    return response.json();
  },

  /**
   * Process writing action (streaming)
   */
  async streamAction(
    action: string,
    content: string,
    projectId: string,
    storyId: string,
    tenantId?: string,
    prompt?: string,
    options?: any
  ) {
    const body: any = { action, content };

    // Add prompt field if action is 'custom' (required) or if provided for other actions
    if (action === 'custom') {
      if (!prompt) {
        throw new Error('Prompt field is mandatory for custom action');
      }
      body.prompt = prompt;
    } else if (prompt) {
      body.prompt = prompt;
    }

    // Add options if provided (reserved for future use)
    if (options) {
      body.options = options;
    }

    return makeAIRequest(
      '/api/writingAction',
      body,
      { stream: true, projectId, storyId, tenantId, baseUrl: GENKIT_BASE_URL }
    );
  }
};

/**
 * AI Outline Rephrasing Service
 */
export const aiOutlineService = {
  /**
   * Rephrase outline (non-streaming)
   */
  async rephraseOutline(outlineText: string, projectId: string, plotcardIds?: string[], options?: any) {
    const body = { outlineText, projectId, plotcardIds, options };
    const response = await makeAIRequest('/api/rephrase-outline', body);
    return response.json();
  },
  
  /**
   * Rephrase outline (streaming)
   */
  async streamRephraseOutline(outlineText: string, projectId: string, plotcardIds?: string[], options?: any) {
    const body = { outlineText, projectId, plotcardIds, options };
    return makeAIRequest('/api/rephrase-outline/stream', body, { stream: true });
  }
};

/**
 * AI Template Service
 */
export const aiTemplateService = {
  /**
   * Create template (non-streaming)
   */
  async createTemplate(message: string, projectId: string, storyId?: string, tenantId?: string, templateData?: any) {
    const body = { message, projectId, ...templateData };
    const response = await makeAIRequest(
      '/functions/v1/template-agent/api/agent/template-generate',
      body,
      { projectId, storyId, tenantId }
    );
    return response.json();
  },

  /**
   * Create template (streaming)
   */
  async streamCreateTemplate(message: string, projectId: string, storyId?: string, tenantId?: string, templateData?: any) {
    const body = { message, projectId, ...templateData };
    return makeAIRequest(
      '/functions/v1/template-agent/api/agent/template-generate',
      body,
      { stream: true, projectId, storyId, tenantId }
    );
  }
};

/**
 * AI Timeline Service
 */
export const aiTimelineService = {
  /**
   * Create timeline (non-streaming)
   */
  async createTimeline(message: string, storyId: string, projectId: string, tenantId?: string) {
    const body = {
      message,
      storyId,
      action: 'create_timeline'
    };
    const response = await makeAIRequest(
      '/functions/v1/timeline-agent/api/agent/timeline',
      body,
      { projectId, storyId, tenantId }
    );
    return response.json();
  },

  /**
   * Create timeline (streaming)
   */
  async streamCreateTimeline(message: string, storyId: string, projectId: string, tenantId?: string) {
    const body = {
      message,
      storyId,
      action: 'create_timeline'
    };
    return makeAIRequest(
      '/functions/v1/timeline-agent/api/agent/timeline',
      body,
      { stream: true, projectId, storyId, tenantId }
    );
  },

  /**
   * Edit/improve timeline (non-streaming)
   */
  async editTimeline(message: string, timelineId: string, storyId: string, projectId: string, tenantId?: string) {
    const body = {
      message,
      timelineId,
      action: 'edit_timeline'
    };
    const response = await makeAIRequest(
      '/functions/v1/timeline-agent/api/agent/timeline',
      body,
      { projectId, storyId, tenantId }
    );
    return response.json();
  },

  /**
   * Edit/improve timeline (streaming)
   */
  async streamEditTimeline(message: string, timelineId: string, storyId: string, projectId: string, tenantId?: string) {
    const body = {
      message,
      timelineId,
      action: 'edit_timeline'
    };
    return makeAIRequest(
      '/functions/v1/timeline-agent/api/agent/timeline',
      body,
      { stream: true, projectId, storyId, tenantId }
    );
  }
};

/**
 * AI Chat Service
 */
export const aiChatService = {
  /**
   * Send chat message (non-streaming)
   */
  async sendMessage(message: string, projectId: string, chatData?: any, storyId?: string, tenantId?: string) {
    const body = { message, projectId, ...chatData };
    const response = await makeAIRequest('/functions/v1/chat-agent/api/agent/chat', body, { projectId, storyId, tenantId });
    return response.json();
  },

  /**
   * Send chat message (streaming)
   */
  async streamMessage(message: string, projectId: string, chatData?: any, storyId?: string, tenantId?: string) {
    const body = { message, projectId, ...chatData };
    return makeAIRequest('/functions/v1/chat-agent/api/agent/chat', body, { stream: true, projectId, storyId, tenantId });
  }
};

/**
 * HITL Timeline Execution Service
 */
export const hitlTimelineService = {
  /**
   * Execute timeline creation from HITL approval data
   */
  async executeTimelineCreation(
    endpoint: {
      url: string;
      method: string;
      headers: Record<string, string>;
      body: any;
    },
    projectId: string,
    storyId?: string,
    tenantId?: string
  ) {
    const headers = await getAuthHeaders(projectId, storyId, tenantId);

    const response = await fetch(`${AI_BASE_URL}${endpoint.url}`, {
      method: endpoint.method,
      headers: {
        ...headers,
        ...endpoint.headers,
      },
      body: JSON.stringify(endpoint.body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Request failed: ${response.statusText}`);
    }

    return response.json();
  }
};

/**
 * AI Entity Service
 */
export const aiEntityService = {
  /**
   * Create entity (non-streaming)
   */
  async createEntity(
    message: string,
    templateId: string,
    categoryId: string,
    projectId: string,
    storyId?: string,
    tenantId?: string,
    contextData?: {
      conversationId?: string;
      entityId?: string;
      entityIds?: string[];
      chapterIds?: string[];
      timelineIds?: string[];
    }
  ) {
    const body = {
      message,
      templateId,
      categoryId,
      ...contextData
    };
    const response = await makeAIRequest(
      '/functions/v1/entity-agent/entity-create',
      body,
      { projectId, storyId, tenantId }
    );
    return response.json();
  },

  /**
   * Create entity (streaming)
   */
  async streamCreateEntity(
    message: string,
    templateId: string,
    categoryId: string,
    projectId: string,
    storyId?: string,
    tenantId?: string,
    contextData?: {
      conversationId?: string;
      entityId?: string;
      entityIds?: string[];
      chapterIds?: string[];
      timelineIds?: string[];
    }
  ) {
    const body = {
      message,
      templateId,
      categoryId,
      ...contextData
    };
    return makeAIRequest(
      '/functions/v1/entity-agent/entity-create',
      body,
      { stream: true, projectId, storyId, tenantId }
    );
  }
};

/**
 * AI Chapter Beats Service
 */
export const aiChapterBeatsService = {
  /**
   * Stream the generation/regeneration of a chapter beat
   */
  async streamGenerateBeat(
    chapterID: number | undefined,
    existingBeats: any[],
    userPrompt?: string,
    beatToRegenerate?: any,
    chapterTitle?: string,
    chapterDescription?: string,
    projectId?: string,
    storyId?: string,
    tenantId?: string
  ) {
    const body: any = { chapterID, existingBeats };
    if (userPrompt) body.userPrompt = userPrompt;
    if (beatToRegenerate) body.beatToRegenerate = beatToRegenerate;
    if (chapterTitle) body.chapterTitle = chapterTitle;
    if (chapterDescription) body.chapterDescription = chapterDescription;

    return makeAIRequest(
      '/api/generate-beat',
      body,
      { stream: true, projectId, storyId, tenantId, baseUrl: GENKIT_BASE_URL }
    );
  }
};

/**
 * Health check for AI service
 */
export const aiHealthService = {
  async checkHealth() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout for health check

      const url = `${AI_BASE_URL}/health`;
      const response = await fetch(url, {
        signal: controller.signal,
        method: 'GET'
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return {
          status: 'unhealthy',
          error: `Service returned ${response.status}`,
          available: false
        };
      }

      const data = await response.json();
      return {
        status: 'healthy',
        available: true,
        ...data
      };
    } catch (error) {
      // Only log in development to avoid console spam
      if (process.env.NODE_ENV === 'development') {
        console.warn('AI Health check failed:', error);
      }
      return {
        status: 'unhealthy',
        error: error instanceof Error ? error.message : 'Unknown error',
        available: false
      };
    }
  },

  async isAvailable() {
    const health = await this.checkHealth();
    return health.available;
  }
};

// Export the base URL for debugging
export { AI_BASE_URL };
